/**
 * AIFormSuggesterDialog (P3-D Doc 39).
 *
 * Modal: textarea intent → AI gen FormSchema JSON → preview + Save & Open Builder.
 */
import { AlertTriangle, Loader2, RefreshCw, Sparkles, X } from "lucide-react";
import * as React from "react";

import { aiAssistApi, type SuggestFormResponse } from "@/api/aiAssist";
import { formsApi } from "@/api/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

export function AIFormSuggesterDialog({
  open,
  onClose,
  onCreated,
}: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const navigate = useRouteStore((s) => s.navigate);

  const [intent, setIntent] = React.useState("");
  const [overrideCode, setOverrideCode] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [result, setResult] = React.useState<SuggestFormResponse | null>(null);

  React.useEffect(() => {
    if (open) {
      setIntent("");
      setOverrideCode("");
      setResult(null);
    }
  }, [open]);

  const handleGenerate = async (): Promise<void> => {
    if (intent.trim().length < 5) {
      pushToast({ variant: "warning", title: "Mô tả intent quá ngắn" });
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const res = await aiAssistApi.suggestForm({
        intent: intent.trim(),
        locale: "vi",
      });
      setResult(res);
      if (!res.ok) {
        pushToast({
          variant: "destructive",
          title: "AI gen thất bại",
          description: res.error ?? "Unknown error",
        });
      }
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Generate thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!result?.ok || !result.schema) return;
    const schema = result.schema as Record<string, unknown>;
    const code =
      overrideCode.trim() ||
      (typeof schema.code === "string" ? schema.code : "");
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(code)) {
      pushToast({
        variant: "warning",
        title: "Code không hợp lệ",
        description: "snake_case [a-z][a-z0-9_]{1,63}",
      });
      return;
    }
    setSaving(true);
    try {
      await formsApi.create({
        code,
        name: typeof schema.title === "string" ? schema.title : code,
        description:
          typeof schema.description === "string" ? schema.description : null,
        schema: { ...schema, code } as never,
      });
      pushToast({
        variant: "success",
        title: `Đã tạo form ${code}`,
        description: "Đang chuyển sang Builder để review...",
      });
      await onCreated();
      onClose();
      navigate("admin.forms.edit", code);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu form thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const schema = result?.schema as
    | { fields?: Array<{ name: string; type: string; label?: string; required?: boolean }> }
    | undefined;
  const fieldCount = schema?.fields?.length ?? 0;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border bg-popover shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b px-4 py-3 bg-gradient-mesh">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-primary" />
            <h2 className="text-sm font-semibold">AI Form Suggester</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-xs font-medium block mb-1">
              Mô tả form bạn muốn:
            </label>
            <Textarea
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              rows={4}
              placeholder='VD: "Form đăng ký nghỉ phép: lý do, ngày bắt đầu, ngày kết thúc, số ngày, người duyệt."'
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Liệt kê các fields cần có. AI tự suy đoán type (text/number/date/select/...) + validation.
            </p>
          </div>

          {!result ? (
            <Button
              variant="brand"
              className="w-full"
              onClick={() => void handleGenerate()}
              disabled={generating || intent.trim().length < 5}
            >
              {generating ? (
                <>
                  <Loader2 className="size-4 mr-1 animate-spin" />
                  AI đang gen form...
                </>
              ) : (
                <>
                  <Sparkles className="size-4 mr-1" /> Generate form
                </>
              )}
            </Button>
          ) : null}

          {result ? (
            <div className="space-y-3 border-t pt-4">
              {result.ok ? (
                <>
                  <div className="rounded-md border bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 p-3 space-y-1">
                    <div className="text-sm font-semibold flex items-center gap-1.5">
                      <Sparkles className="size-3.5 text-emerald-600" />
                      Đã gen form
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <code className="bg-muted px-1 rounded">
                        {String(schema?.["code" as never] ?? "no_code")}
                      </code>
                      {" · "}
                      <span>{fieldCount} fields</span>
                    </div>
                  </div>

                  {result.explanation ? (
                    <div className="text-xs text-foreground bg-muted/50 rounded-md p-2.5">
                      <div className="font-medium mb-0.5">AI giải thích:</div>
                      <p className="text-muted-foreground">{result.explanation}</p>
                    </div>
                  ) : null}

                  {/* Fields preview */}
                  {schema?.fields && schema.fields.length > 0 ? (
                    <div className="border rounded-md p-2 max-h-[200px] overflow-y-auto bg-muted/20">
                      <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                        Fields
                      </div>
                      <ul className="space-y-0.5 text-xs">
                        {schema.fields.map((f) => (
                          <li key={f.name} className="flex items-center gap-1.5">
                            <span className="inline-block w-20 text-[10px] text-muted-foreground uppercase truncate">
                              {f.type}
                            </span>
                            <code className="text-[10px] bg-muted px-1 rounded">
                              {f.name}
                            </code>
                            <span className="truncate">{f.label}</span>
                            {f.required ? (
                              <span className="text-[9px] text-destructive">
                                required
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {result.warnings && result.warnings.length > 0 ? (
                    <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800 p-2.5 text-xs">
                      <div className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-100">
                        <AlertTriangle className="size-3.5" />
                        Cảnh báo ({result.warnings.length})
                      </div>
                      <ul className="mt-1 space-y-0.5 text-amber-900 dark:text-amber-200 list-disc list-inside">
                        {result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div>
                    <label className="text-[10px] font-medium block mb-0.5 text-muted-foreground">
                      Code form (override)
                    </label>
                    <Input
                      value={overrideCode}
                      onChange={(e) =>
                        setOverrideCode(
                          e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9_]/g, "_"),
                        )
                      }
                      placeholder={
                        typeof schema?.["code" as never] === "string"
                          ? String(schema?.["code" as never])
                          : "snake_case_code"
                      }
                      className="font-mono text-sm h-8"
                    />
                  </div>
                </>
              ) : (
                <div className="rounded-md border bg-rose-50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800 p-3 space-y-1">
                  <div className="text-sm font-semibold text-rose-900 dark:text-rose-100 flex items-center gap-1.5">
                    <AlertTriangle className="size-3.5" />
                    AI gen thất bại
                  </div>
                  <p className="text-xs text-rose-800 dark:text-rose-200">
                    {result.error ?? "Unknown error"}
                  </p>
                  {result.raw_text ? (
                    <details className="text-[10px] mt-2">
                      <summary className="cursor-pointer text-muted-foreground">
                        Xem raw response
                      </summary>
                      <pre className="mt-1 bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap text-[10px]">
                        {result.raw_text}
                      </pre>
                    </details>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </div>

        <footer className="flex justify-between gap-2 border-t px-4 py-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Huỷ
          </Button>
          <div className="flex gap-2">
            {result ? (
              <Button
                variant="outline"
                onClick={() => void handleGenerate()}
                disabled={generating || saving}
              >
                <RefreshCw className="size-3.5 mr-1" />
                Regenerate
              </Button>
            ) : null}
            {result?.ok ? (
              <Button
                variant="brand"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="size-3.5 mr-1 animate-spin" />
                    Đang lưu...
                  </>
                ) : (
                  "Save & Open Builder"
                )}
              </Button>
            ) : null}
          </div>
        </footer>
      </div>
    </div>
  );
}
